import MaterialTable from '@material-table/core'
import {
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Button,
  CircularProgress,
} from '@material-ui/core'
import { Container } from 'components/Layout/Container'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { useMaterialTableLocalization } from 'customHooks/useMaterialTableLocalization'
import { useAppRunningContext } from 'features/EmbeddedRh24/useAppRunningContext'
import { HTMLProps, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { RootAppState, useAppSelector } from 'store/configureStore'
import { useLocation, useNavigate } from 'react-router-dom'
import { useController } from 'customHooks/useController'
import { UserController } from 'features/CustomerPortal/ConnectedParties/controllers/UserController'
import { ShowException } from 'store/Application/appActions'
import { AppLogo } from 'components/Layout/AppLogo'
import { isEqual } from 'lodash'
import { useAuth0 } from '@auth0/auth0-react'
import Spinning from 'components/Common/Spinner/Spinning'
import { AuthService } from 'services/auth'

const PageTitle = (props: HTMLProps<HTMLDivElement>) => (
  <div {...props}>
    <LocalizedTypography
      translationKey="common:my-workspaces"
      variant="h4"
      color="primary"
    >
      workspaces
    </LocalizedTypography>
    <LocalizedTypography
      translationKey="common:my-workspaces--subtitle"
      variant="subtitle1"
      color="textSecondary"
      component="p"
    >
      select a workspace to start
    </LocalizedTypography>
  </div>
)

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    workspacesRoot: {
      padding: theme.spacing(3, 0),
      overflow: 'auto',
      height: '100%',
      width: '100%',
    },
    pageTitle: {
      marginBottom: theme.spacing(4),
    },
    workspaces: {
      display: 'flex',
      justifyContent: 'space-around',
      flexWrap: 'wrap',
      gap: theme.spacing(4),
    },
    workspaceTable: {
      flex: `1 1 ${theme.breakpoints.values.xs}px`,
    },
    noWorkspaces: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      gap: theme.spacing(2),
      textAlign: 'center',
      '& img': {
        maxWidth: '120px',
      },
      '& div': {
        textAlign: 'center',
        textTransform: 'lowercase',
      },
    },
  })
)

const Selectors = {
  organizations: (state: RootAppState) => state.user.organizations,
  availablePortals: (state: RootAppState) => state.user.availablePortals,
}

const WorkspacePageException = (error) => ShowException('Workspaces', error)

const Workspaces = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const classes = useStyles()
  const materialTableLocalization = useMaterialTableLocalization()

  const { controller, loading } = useController(() => new UserController())

  const organizations = useAppSelector(Selectors.organizations, isEqual)
  const availablePortals = useAppSelector(Selectors.availablePortals, isEqual)
  const { withinIframe } = useAppRunningContext()

  const auth0 = useAuth0()

  const getProfileData = useCallback(
    () =>
      controller
        .GetProfile({
          returnUrl: location.state?.returnUrl || '/app',
          onNavigate: navigate,
        })
        .catch(WorkspacePageException),
    [controller, location.state?.returnUrl, navigate]
  )

  const selectWorkspace = (workspaceId: string) =>
    controller
      .SelectWorkspace(workspaceId)
      .then(() => navigate(location.state?.returnUrl || '/app'))
      .catch(WorkspacePageException)

  useEffect(() => {
    if (!auth0.isLoading && auth0.isAuthenticated && !organizations) {
      AuthService.Build(auth0)
      getProfileData()
    }
  }, [
    auth0,
    auth0.isAuthenticated,
    auth0.isLoading,
    getProfileData,
    organizations,
  ])

  if (auth0.isLoading || !organizations) {
    return <Spinning />
  }

  if (
    organizations?.length === 0 &&
    (!availablePortals || availablePortals?.length === 0)
  ) {
    return (
      <div className={classes.workspacesRoot}>
        <Container className={classes.noWorkspaces} maxWidth={'sm'}>
          <AppLogo />
          <div>
            <Typography>
              {t(
                'common:workspaces-no-workspaces',
                'your user needs a Workspace to start using the system.'
              )}
            </Typography>
            <Typography>
              {t(
                'common:invitation-retry',
                'retry the invitation process or contact your administrator.'
              )}
            </Typography>
          </div>
          <Button
            color="primary"
            onClick={() => getProfileData()}
            variant="outlined"
            fullWidth
            startIcon={
              loading['get-profile'] ? <CircularProgress size={16} /> : null
            }
          >
            {t('common:workspaces-retry-get-data', 'Retry get user data')}
          </Button>
          <Button
            onClick={() => controller.Logout()}
            color="primary"
            variant="contained"
            fullWidth
          >
            {t('common:logout')}
          </Button>
        </Container>
      </div>
    )
  }

  return (
    <div className={classes.workspacesRoot}>
      <Container>
        <PageTitle className={classes.pageTitle} />
        {/* <Typography>Last visited workspace {lastVisitedWorkspace}</Typography> */}
        <div className={classes.workspaces}>
          {organizations?.length ? (
            <div className={classes.workspaceTable}>
              <MaterialTable
                title={t('common:factories')}
                options={{
                  showTitle: !withinIframe,
                  paging: false,
                  header: false,
                  maxBodyHeight: 'calc(100vh - 280px)',
                }}
                data={organizations?.map((o) => ({ ...o }))}
                localization={materialTableLocalization}
                isLoading={loading['get-profile']}
                columns={[
                  {
                    field: 'name',
                  },
                ]}
                actions={[
                  {
                    icon: 'refresh_outlined',
                    onClick: () => getProfileData(),
                    disabled: loading['get-profile'],
                    isFreeAction: true,
                  },
                ]}
                onRowClick={(e, row) => {
                  selectWorkspace(row.id)
                }}
              />
            </div>
          ) : null}
          {availablePortals?.length ? (
            <div className={classes.workspaceTable}>
              <MaterialTable
                title={t('common:self-service-portal', 'self service portal')}
                isLoading={loading['get-profile']}
                options={{
                  showTitle: true,
                  paging: false,
                  header: false,
                  defaultOrderByCollection: [],
                }}
                data={availablePortals.map((x) => ({ ...x, id: x.url }))}
                localization={materialTableLocalization}
                columns={[
                  {
                    field: 'companyName',
                  },
                  {
                    field: 'url',
                  },
                ]}
                actions={[
                  {
                    icon: 'refresh_outlined',
                    onClick: () => getProfileData(),
                    isFreeAction: true,
                  },
                ]}
                onRowClick={(_, row) => window.location.replace(row.url)}
              />
            </div>
          ) : null}
        </div>
      </Container>
    </div>
  )
}

export default Workspaces
